export const NAV_ITEMS = [
  { label: 'Platform', to: '/alt6/platform' },
  { label: 'Autopilot', to: '/alt6/autopilot' },
  { label: 'Patient journey', to: '/alt6/patient-journey' },
  { label: 'Switching', to: '/alt6/switching' },
  { label: 'Why Asha', to: '/alt6/why-asha' },
];

export const DEMO_PATH = '/alt6/demo';

// Scripted events for the hero autopilot simulation. Each event runs
// through working → done so the feed reads like a live office.
export const HERO_FEED = [
  {
    agent: 'Reception',
    working: 'Answering an inbound call',
    done: 'New patient booked · Thu 9:40 AM',
  },
  {
    agent: 'Insurance',
    working: "Verifying benefits for tomorrow's schedule",
    done: '12 of 12 visits verified',
  },
  {
    agent: 'Scheduling',
    working: 'Filling the 2:10 PM opening',
    done: 'Chair filled from the waitlist',
  },
  {
    agent: 'Claims',
    working: "Scrubbing and submitting today's claims",
    done: '9 claims out the door, clean',
  },
  {
    agent: 'EOB posting',
    working: 'Posting payments from received EOBs',
    done: '14 EOBs posted and reconciled',
  },
  {
    agent: 'Denials',
    working: 'Drafting an appeal for a denied crown',
    done: 'Appeal packet sent to the payer',
  },
  {
    agent: 'Recall',
    working: 'Reaching patients overdue for hygiene',
    done: '6 hygiene visits back on the books',
  },
  {
    agent: 'Billing',
    working: 'Texting balance reminders with pay links',
    done: 'Statements out, replies handled',
  },
];

export const HERO_SCHEDULE = [
  { time: '8:00', patient: 'Hygiene · M. R.', status: 'confirmed' },
  { time: '9:40', patient: 'New patient exam', status: 'booked' },
  { time: '11:20', patient: 'Crown seat · J. T.', status: 'verified' },
  { time: '2:10', patient: 'Open chair', status: 'filling' },
  { time: '3:30', patient: 'Perio maintenance · A. S.', status: 'confirmed' },
  { time: '4:40', patient: 'Whitening consult · D. K.', status: 'booked' },
];

export const PAIN_POINTS = [
  {
    title: 'The phones never stop',
    body: 'Calls stack up, holds run long, and every voicemail that goes unreturned is a patient who found another office.',
  },
  {
    title: 'The desk depends on who showed up',
    body: 'One sick day and verifications, follow-ups, and confirmations slip. The practice should not run on attendance.',
  },
  {
    title: 'The revenue cycle leaks quietly',
    body: 'Denied claims sit unworked, EOBs pile up, balances age out. Money you already earned never lands.',
  },
  {
    title: 'Chairs sit empty',
    body: 'No-shows and open blocks are rebooked by hand — when someone has a spare minute, which is never.',
  },
  {
    title: 'Patients drift away',
    body: 'Treatment plans go home unaccepted and hygiene recall slips through the cracks, one quiet month at a time.',
  },
  {
    title: 'Ten tools, none of them talking',
    body: 'A PMS here, phones there, forms somewhere else. Double entry everywhere, and no single source of truth.',
  },
];

export const SOFTWARE_MODULES = [
  {
    name: 'Practice management core',
    body: 'Scheduling, clinical charting, treatment plans, perio charting, and patient ledgers — the system of record.',
  },
  {
    name: 'Phones & VoIP',
    body: 'Desk phones, smart routing, recording, and voice logs that land in the patient record automatically.',
  },
  {
    name: 'Two-way messaging',
    body: 'Texting, email, and a universal inbox — every patient conversation in one place, with AI drafting replies.',
  },
  {
    name: 'Digital forms & intake',
    body: 'Next-gen forms that fill the chart directly. No clipboards, no scanning, no retyping.',
  },
  {
    name: 'Online scheduling',
    body: 'Patients self-book from real-time availability, any hour of the night.',
  },
  {
    name: 'Cloud imaging',
    body: 'DICOM-compatible imaging in the same chart as everything else. No bridge software, no server closet.',
  },
  {
    name: 'eClaims & clearinghouse',
    body: 'Claims are scrubbed, submitted, and tracked from inside the ledger — one login, no third-party portal.',
  },
  {
    name: 'Payments & text-to-pay',
    body: 'Card readers, pay-by-link, and automated reminders that reconcile back to the ledger on their own.',
  },
  {
    name: 'Patient portal',
    body: 'Secure links for forms, treatment plans, and insurance uploads — no passwords for patients to forget.',
  },
  {
    name: 'ePrescribe',
    body: 'Electronic prescriptions from inside the chart.',
  },
  {
    name: 'Intelligent fax',
    body: 'Inbound faxes are read, classified, and filed to the right patient automatically.',
  },
  {
    name: 'Campaigns & analytics',
    body: 'Clinically-aware SMS and email campaigns, plus dashboards for production, collections, and aging.',
  },
];

export const AI_CAPABILITIES = [
  {
    area: 'Front office',
    items: [
      {
        name: 'Answering every call',
        body: '24/7, in multiple languages, booking straight into the schedule — no hold music, no voicemail graveyard.',
      },
      {
        name: 'Keeping chairs full',
        body: 'Open blocks and no-shows are rebooked from the waitlist before anyone notices the gap.',
      },
      {
        name: 'Bringing patients back',
        body: 'Overdue hygiene patients get thoughtful, persistent outreach until they are back on the books.',
      },
      {
        name: 'Following up on treatment',
        body: 'Patients who left without accepting a plan hear from the practice — with context, not a generic blast.',
      },
      {
        name: 'Intake before the visit',
        body: 'Health history, consents, and insurance details are collected and charted before the patient arrives.',
      },
    ],
  },
  {
    area: 'Revenue cycle',
    items: [
      {
        name: 'Verifying insurance',
        body: 'Eligibility checks and full benefit breakdowns for every visit on the schedule — including calling the payer when portals fall short.',
      },
      {
        name: 'Filing clean claims',
        body: 'Claims are scrubbed against payer rules and submitted the day of service.',
      },
      {
        name: 'Posting EOBs',
        body: 'Payments are posted, reconciled, and flagged when something looks off.',
      },
      {
        name: 'Working denials',
        body: 'Denials are analyzed, appealed, and tracked to resolution instead of aging in a folder.',
      },
      {
        name: 'Collecting balances',
        body: 'Patient balances get friendly, well-timed reminders with a payment link — not a surprise statement.',
      },
    ],
  },
  {
    area: 'Clinical support',
    items: [
      {
        name: 'Writing the notes',
        body: 'Visits are transcribed into structured clinical notes while the provider stays chairside.',
      },
      {
        name: 'Voice perio charting',
        body: 'Call out pocket depths; the chart fills itself in.',
      },
      {
        name: 'Real-time summaries',
        body: 'Providers see what happened across the practice without chasing anyone down.',
      },
    ],
  },
];

export const AUTOPILOT_CHECKLIST = [
  {
    title: 'A sick day is just a sick day',
    body: 'If your front desk called out for the rest of the week, nothing critical would slip — calls answered, visits verified, claims filed.',
  },
  {
    title: 'Growth without a hiring spree',
    body: 'If two hundred new patients showed up next month, you would not need to add admin staff to absorb them.',
  },
  {
    title: 'Nothing leaks',
    body: 'Denials get worked, EOBs get posted, balances get collected — not because someone remembered, but because the system never forgets.',
  },
];

export const LOOP_STEPS = [
  {
    name: 'Reason',
    body: 'ARIA looks at the state of the practice and decides what needs to happen next — "eight visits tomorrow still need verification."',
  },
  {
    name: 'Act',
    body: 'It dispatches the right specialist: check the portal, call the payer, text the patient, file the claim.',
  },
  {
    name: 'Observe',
    body: 'It reads the result — seven verified, one payer needs a phone call — and decides the next step.',
  },
  {
    name: 'Repeat',
    body: 'The loop runs as many times as the job requires, until the work is actually done.',
  },
];

export const JOURNEY_STEPS = [
  {
    phase: 'First contact',
    title: 'The call gets answered',
    body: 'A new patient calls at 8:45 PM. The AI answers, checks real availability, collects insurance details, and books the visit. A confirmation text is already on its way.',
    handled: 'Call answered · visit booked · insurance captured',
  },
  {
    phase: 'Before the visit',
    title: 'Intake happens at home',
    body: 'Health history, consents, and photos of the insurance card come in through a secure link — and land directly in the chart. Benefits are verified and broken down before the patient walks in.',
    handled: 'Forms charted · benefits verified · estimate ready',
  },
  {
    phase: 'Day of visit',
    title: 'The visit stays clinical',
    body: 'The provider talks to the patient, not the keyboard. Notes are transcribed, perio charts fill by voice, and the treatment plan is ready to present at checkout.',
    handled: 'Notes written · chart updated · plan presented',
  },
  {
    phase: 'Checkout',
    title: 'Payment without the awkward part',
    body: 'The patient pays by card or text-to-pay link. The claim is scrubbed and submitted before the parking lot clears.',
    handled: 'Payment collected · claim submitted same day',
  },
  {
    phase: 'After the visit',
    title: 'The money actually arrives',
    body: 'The EOB comes back, gets posted, and reconciles against the ledger. If the claim is denied, an appeal goes out — tracked until it resolves.',
    handled: 'EOB posted · denial appealed · ledger clean',
  },
  {
    phase: 'The long run',
    title: 'Nobody falls through the cracks',
    body: 'Six months later, recall outreach starts on its own. Unaccepted treatment gets a follow-up. The patient comes back — without anyone at the desk keeping a list.',
    handled: 'Recall scheduled · treatment followed up',
  },
];

export const MIGRATION_STEPS = [
  {
    step: '01',
    title: 'Map your practice',
    body: 'We learn your schedule templates, fee schedules, payer mix, and workflows — before anything moves.',
  },
  {
    step: '02',
    title: 'Migrate the data',
    body: 'Patients, charts, ledgers, imaging, and documents come over in full. Your history stays your history.',
  },
  {
    step: '03',
    title: 'Verify everything',
    body: 'We reconcile the migrated data against your current system and walk through it with you, chart by chart if you want.',
  },
  {
    step: '04',
    title: 'Train the team',
    body: 'White-glove onboarding for every role — front desk, clinical, and billing — with a dedicated migration lead.',
  },
  {
    step: '05',
    title: 'Go live, then calibrate',
    body: 'You launch in about four weeks. Autopilot ramps up gradually: the AI takes on more as you build trust in it.',
  },
];

export const GUARDRAILS = [
  {
    title: 'You set the boundaries',
    body: 'Every workflow has a policy: what the AI may do on its own, and what waits for a person to approve.',
  },
  {
    title: 'A review queue, not a black box',
    body: 'Anything ambiguous — an odd payer response, an upset patient — is routed to your team with full context.',
  },
  {
    title: 'Every action on the record',
    body: 'Calls, messages, claims, and postings are logged in the patient record with a full audit trail.',
  },
  {
    title: 'HIPAA, end to end',
    body: 'Built for protected health information from the first line of code — not retrofitted for it.',
  },
];
